import React from 'react';
import { Typography, Row, Col, Button } from 'antd';
import { ReactComponent as GeocloudLogo } from '~/assets/svg/geocloud_logo.svg';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

// Footer links - sẽ được translate trong component
const getFooterLinks = (t: any) => ({
    Product: [
        { key: 'Web GIS', label: 'Web GIS' },
        { key: 'Dashboards', label: 'Dashboards' },
        { key: 'Cloud Sources', label: 'Cloud Sources' },
        { key: 'App Development', label: 'App Development' },
        { key: 'Raster', label: 'Raster' },
        { key: 'Felt AI', label: 'Felt AI' },
    ],
    Industries: [
        { key: 'Agriculture', label: 'Agriculture' },
        { key: 'Cities and Government', label: 'Cities and Government' },
        { key: 'Climate', label: 'Climate' },
        { key: 'Education', label: 'Education' },
        { key: 'Energy', label: 'Energy' },
        { key: 'Real Estate', label: 'Real Estate' },
    ],
    Resources: [
        { key: 'Pricing', label: 'Pricing' },
        { key: 'Help Center', label: 'Help Center' },
        { key: 'Developer Docs', label: 'Developer Docs' },
        { key: 'Customers', label: 'Customers' },
        { key: 'Blog', label: 'Blog' },
        { key: 'Map Gallery', label: 'Map Gallery' },
    ],
    Community: [
        { key: 'Reddit', label: 'Reddit' },
        { key: 'LinkedIn', label: 'LinkedIn' },
        { key: 'YouTube', label: 'YouTube' },
        { key: 'Twitter', label: 'Twitter' },
        { key: 'Slack', label: 'Slack' },
        { key: 'Facebook', label: 'Facebook' },
    ],
    Company: [
        { key: 'About', label: 'About' },
        { key: 'Careers', label: 'Careers' },
        { key: 'Media Kit', label: 'Media Kit' },
        { key: 'Security', label: 'Security' },
        { key: 'Privacy', label: 'Privacy' },
        { key: 'Terms', label: 'Terms' },
    ],
});

const FooterSection: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const footerLinks = getFooterLinks(t);

    const getCategoryLabel = (category: string) => {
        const categoryMap: { [key: string]: string } = {
            Product: 'landing__menu_product',
            Industries: 'landing__menu_industries',
            Resources: 'landing__menu_resources',
            Community: 'landing__menu_community',
            Company: 'landing__menu_company',
        };
        return t(categoryMap[category] || category);
    };

    return (
        <div
            style={{
                position: 'relative',
                zIndex: 1,
                padding: '40px 20px 20px',
                background: 'rgba(0,0,0,0.3)',
                borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
        >
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                {/* Logo & Sign Up */}
                <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                    <Col xs={24} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                <GeocloudLogo style={{ height: 32, marginRight: 10 }} />
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>GeoCloud</span>
                                    <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>Cloud Native GIS</span>
                                </div>
                            </div>
                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', display: 'block', marginBottom: 12 }}>
                                {t('common_app_description')}
                            </Text>
                            <Button
                                type="primary"
                                style={{
                                    background: 'linear-gradient(135deg, #69c0ff, #d3adf7)',
                                    border: 'none',
                                    color: '#000',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                }}
                            >
                                {t('landing__footer_signup')}
                            </Button>
                        </motion.div>
                    </Col>

                    {/* Links */}
                    {Object.entries(footerLinks).map(([category, links], colIndex) => (
                        <Col xs={12} sm={6} md={3} key={category}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: colIndex * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div style={{ color: '#fff', fontWeight: 600, marginBottom: 12, fontSize: '0.85rem' }}>
                                    {getCategoryLabel(category)}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {links.map((link, index) => (
                                        <Text
                                            key={index}
                                            style={{
                                                color: 'rgba(255,255,255,0.6)',
                                                fontSize: '0.8rem',
                                                cursor: 'pointer',
                                                transition: 'color 0.2s',
                                            }}
                                            onMouseEnter={(e) => {
                                                (e.target as HTMLElement).style.color = '#69c0ff';
                                            }}
                                            onMouseLeave={(e) => {
                                                (e.target as HTMLElement).style.color = 'rgba(255,255,255,0.6)';
                                            }}
                                        >
                                            {link.label}
                                        </Text>
                                    ))}
                                </div>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* Copyright */}
                <div
                    style={{
                        textAlign: 'center',
                        paddingTop: 20,
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                        {t('landing__footer_copyright')}
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default FooterSection;
